import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Button } from "@/components/ui/button";
import { Sparkles, X, Check, Lock } from 'lucide-react';

export default function AISeoSuggestions({ formData, onApply, acceptedFields = {}, onFieldAccepted }) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [applied, setApplied] = useState({});

  const buildGalleryInfo = () => {
    const imgs = formData.gallery_images || [];
    const metas = formData.gallery_images_meta || [];
    if (imgs.length === 0) return '(nessuna immagine in galleria)';
    return imgs.map((url, i) => {
      const m = metas[i] || {};
      return `  Galleria ${i + 1}: alt="${m.title || 'vuoto'}", desc="${m.description || 'vuoto'}", tag="${m.tags || 'vuoto'}"`;
    }).join('\n');
  };

  const buildAcceptedInfo = () => {
    const keys = Object.keys(acceptedFields).filter(k => acceptedFields[k]);
    if (keys.length === 0) return '(nessuno)';
    return keys.join(', ');
  };

  const handleAsk = async () => {
    setLoading(true);
    setSuggestions(null);
    try {
      const result = await base44.integrations.Core.InvokeLLM({
        prompt: `Sei un esperto SEO per studi di architettura italiani. Analizza i dati di questo progetto architettonico e fornisci suggerimenti concreti per migliorare il posizionamento SEO su Google.

Dati del progetto:
- Titolo: ${formData.title || '(vuoto)'}
- Categoria: ${formData.category || '(vuoto)'}
- Anno: ${formData.year || '(vuoto)'}
- Luogo: ${formData.location || '(vuoto)'}
- Cliente: ${formData.client || '(vuoto)'}
- Superficie: ${formData.area || '(vuoto)'}
- Fotografo: ${formData.photographer || '(vuoto)'}
- Descrizione breve: ${formData.short_description || '(vuoto)'}
- Descrizione completa: ${formData.description || '(vuoto)'}
- Alt text copertina: ${formData.cover_image_meta?.title || '(vuoto)'}
- Descrizione copertina: ${formData.cover_image_meta?.description || '(vuoto)'}
- Tag copertina: ${formData.cover_image_meta?.tags || '(vuoto)'}
- Immagini galleria:
${buildGalleryInfo()}

CAMPI GIÀ OTTIMIZZATI E ACCETTATI DALL'UTENTE (NON suggerire modifiche per questi):
${buildAcceptedInfo()}

Fornisci suggerimenti pratici e specifici per i campi NON già ottimizzati. Per ogni suggerimento DEVI includere il campo field_key che indica esattamente quale campo del form modificare.

I valori possibili per field_key sono SOLO questi:
- "title" → titolo del progetto
- "short_description" → descrizione breve
- "description" → descrizione completa
- "cover_image_meta.title" → alt text / titolo SEO dell'immagine di copertina
- "cover_image_meta.description" → descrizione SEO dell'immagine di copertina
- "cover_image_meta.tags" → parole chiave/tag dell'immagine di copertina (stringa separata da virgole)
${(formData.gallery_images || []).map((_, i) => `- "gallery_images_meta.${i}.title" → alt text galleria ${i + 1}\n- "gallery_images_meta.${i}.description" → descrizione galleria ${i + 1}\n- "gallery_images_meta.${i}.tags" → tag galleria ${i + 1}`).join('\n')}

REGOLE IMPORTANTI:
- NON suggerire modifiche per i campi già ottimizzati elencati sopra.
- Non inventare field_key diversi da quelli elencati sopra.
- Il campo "example" deve essere una STRINGA di testo (non un array, non un oggetto).
- Per i campi "tags" scrivi i tag separati da virgola: "architettura, Palermo, interior design".
- Fornisci solo suggerimenti per i field_key elencati sopra.`,
        response_json_schema: {
          type: "object",
          properties: {
            overall_score: { type: "number", description: "Punteggio SEO attuale da 1 a 10" },
            summary: { type: "string", description: "Valutazione generale in 2 righe" },
            suggestions: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  field: { type: "string", description: "Nome leggibile del campo" },
                  field_key: { type: "string", description: "Chiave esatta del campo nel form" },
                  issue: { type: "string" },
                  suggestion: { type: "string" },
                  example: { type: "string", description: "Testo esatto da inserire nel campo, pronto all'uso" }
                }
              }
            }
          }
        }
      });
      // Filter out already-accepted fields from results
      if (result?.suggestions) {
        result.suggestions = result.suggestions.filter(s => !acceptedFields[s.field_key]);
      }
      setSuggestions(result);
    } catch (e) {
      setSuggestions({ error: 'Errore nella generazione dei suggerimenti. Riprova.' });
    }
    setLoading(false);
  };

  const handleApply = (s, i) => {
    if (!s.field_key || !s.example) return;
    const normalizedValue = Array.isArray(s.example) ? s.example.join(', ') : s.example;
    onApply(s.field_key, normalizedValue);
    setApplied(prev => ({ ...prev, [i]: true }));
    // Notify parent that this field is now accepted/locked
    if (onFieldAccepted) onFieldAccepted(s.field_key);
  };

  const scoreColor = (score) => {
    if (score >= 7) return 'text-green-600';
    if (score >= 4) return 'text-yellow-600';
    return 'text-red-600';
  };

  const acceptedKeys = Object.keys(acceptedFields).filter(k => acceptedFields[k]);

  return (
    <div className="border border-[#9c8b7a]/30 rounded-lg bg-[#faf9f7] p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#9c8b7a]" />
          <span className="font-medium text-[#2d2d2d]">Suggerimenti SEO con AI</span>
        </div>
        {suggestions && (
          <button onClick={() => setSuggestions(null)} className="text-[#7a7a7a] hover:text-[#2d2d2d]">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Show locked/accepted fields */}
      {acceptedKeys.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1">
          {acceptedKeys.map(k => (
            <span key={k} className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 rounded-full px-2 py-0.5">
              <Lock className="w-2.5 h-2.5" /> {k}
            </span>
          ))}
        </div>
      )}

      {!suggestions && (
        <>
          <p className="text-sm text-[#7a7a7a] mb-3">
            L'AI analizzerà tutti i campi compilati e suggerirà modifiche per migliorare il posizionamento su Google.
            {acceptedKeys.length > 0 && ` I campi già ottimizzati (${acceptedKeys.length}) non verranno riproposti.`}
          </p>
          <Button
            type="button"
            onClick={handleAsk}
            disabled={loading}
            className="bg-[#9c8b7a] hover:bg-[#8a7a6a] text-white"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {loading ? 'Analisi in corso...' : 'Chiedi suggerimenti all\'AI'}
          </Button>
        </>
      )}

      {suggestions && suggestions.error && (
        <p className="text-sm text-red-600">{suggestions.error}</p>
      )}

      {suggestions && !suggestions.error && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className={`text-3xl font-bold ${scoreColor(suggestions.overall_score)}`}>
              {suggestions.overall_score}/10
            </span>
            <p className="text-sm text-[#7a7a7a]">{suggestions.summary}</p>
          </div>

          {suggestions.suggestions?.length > 0 ? (
            <div className="space-y-3">
              {suggestions.suggestions.map((s, i) => (
                <div key={i} className={`bg-white rounded border p-3 transition-colors ${applied[i] ? 'border-green-300 bg-green-50' : 'border-[#e5e5e5]'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-[#9c8b7a]">{s.field}</span>
                    {s.example && s.field_key && (
                      <Button
                        type="button"
                        size="sm"
                        variant={applied[i] ? "secondary" : "outline"}
                        className={`h-7 text-xs ${applied[i] ? 'text-green-700' : ''}`}
                        onClick={() => handleApply(s, i)}
                        disabled={applied[i]}
                      >
                        <Check className="w-3 h-3 mr-1" />
                        {applied[i] ? 'Applicato ✓' : 'Applica Suggerimento'}
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-red-600 mb-1">⚠ {s.issue}</p>
                  <p className="text-sm text-[#2d2d2d] mb-1">{s.suggestion}</p>
                  {s.example && (
                    <p className="text-xs text-[#7a7a7a] italic bg-[#f8f8f8] rounded px-2 py-1">
                      Testo suggerito: "{s.example}"
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-green-700 bg-green-50 rounded p-3">
              ✓ Tutti i campi sono già ottimizzati! Nessun ulteriore suggerimento.
            </p>
          )}

          <Button type="button" variant="outline" size="sm" onClick={handleAsk} disabled={loading}>
            <Sparkles className="w-3 h-3 mr-1" /> Rigenera
          </Button>
        </div>
      )}
    </div>
  );
}